import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getRoleAssignments, getAvailableRoles, getManageableQueues } from '../firebase';
import { getUserPermissions, canAccessOU } from '../utils/permissions';
import { validateDomainAccess, canAccessApplication } from '../utils/domainAccess';
import { APP_CONFIG } from '../config/app';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(['*']); // Default to super admin
  const [userOUs, setUserOUs] = useState(['*']); // Default to all OUs
  const [userRole, setUserRole] = useState('super_admin'); // Default role
  const [manageableQueues, setManageableQueues] = useState(['*']); // Phase 3: Queues user can manage
  const [loading, setLoading] = useState(true);
  const [domainAccess, setDomainAccess] = useState({ allowed: false, reason: '' });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 Auth state changed:', user ? user.email : 'No user');
      setUser(user);
      
      if (user) {
        // Check if we're in internal mode (bypass verification)
        if (APP_CONFIG.INTERNAL_MODE) {
          console.log('🏢 Internal mode: Bypassing verification for:', user.email);
          
          // Check domain access only
          const domainValidation = validateDomainAccess(user);
          setDomainAccess(domainValidation);
          
          if (domainValidation.allowed) {
            // Grant full permissions for internal users
            setUserPermissions(['*']);
            setUserOUs(['*']);
            setUserRole('super_admin');
            setManageableQueues(['*']); // Phase 3: Super admin can manage all queues
            console.log('🔑 Internal user granted full permissions');
          } else {
            console.log('🚫 Domain access denied for internal user');
            setUserPermissions([]);
            setUserOUs([]);
            setUserRole(null);
            setManageableQueues([]);
          }
        } else {
          // Normal verification process
          console.log('🌐 Validating domain access for:', user.email);
          const domainValidation = validateDomainAccess(user);
          console.log('🌐 Domain validation result:', domainValidation);
          setDomainAccess(domainValidation);
          
          if (domainValidation.allowed) {
            try {
              // Get the user's Google OAuth access token
              const credential = await user.getIdTokenResult();
              console.log('🎫 User credential obtained:', !!credential);
              
              // Try to load real permissions from Firebase Functions
              await loadUserPermissions(user);
              
              // Phase 3: Load manageable queues
              await loadManageableQueues(user);
              
            } catch (error) {
              console.error('💥 Error in auth flow:', error);
              // Set default permissions for super admin
              setUserPermissions(['*']);
              setUserOUs(['*']);
              setUserRole('super_admin');
              setManageableQueues(['*']); // Phase 3: Super admin can manage all queues
              console.log('🔑 Set emergency fallback permissions: [*]');
            }
          } else {
            console.log('🚫 Domain access denied');
            // Clear permissions for unauthorized users
            setUserPermissions([]);
            setUserOUs([]);
            setUserRole(null);
            setManageableQueues([]);
          }
        }
      } else {
        console.log('👤 No user logged in');
        setUserPermissions([]);
        setUserOUs([]);
        setUserRole(null);
        setManageableQueues([]);
        setDomainAccess({ allowed: false, reason: '' });
      }
      
      console.log('✅ Auth flow complete, setting loading to false');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Helper function to map Google role IDs to our app roles
  const mapGoogleRoleToAppRole = (googleRoleId) => {
    // Map common Google Admin role IDs to our app roles
    // You might need to adjust these based on your actual role IDs
    if (googleRoleId && googleRoleId.includes('SUPER_ADMIN')) return 'super_admin';
    if (googleRoleId && googleRoleId.includes('ORG_UNIT_ADMIN')) return 'ou_admin';
    if (googleRoleId && googleRoleId.includes('USER_ADMIN')) return 'user_admin';
    return 'viewer'; // Default fallback
  };

  const loadUserPermissions = async (user) => {
    console.log('🔄 Loading user permissions for:', user.email);
    try {
      // Try to get real role assignments from Firebase Functions
      console.log('📞 Calling getRoleAssignments...');
      const roleResponse = await getRoleAssignments();
      console.log('📋 Role response:', roleResponse);
      
      if (roleResponse.data.success) {
        console.log('✅ Role assignments loaded successfully');
        const userRoles = roleResponse.data.data.filter(
          role => role.assignedTo === user.email
        );
        console.log('👤 User roles found:', userRoles.length);
        
        // Check if this is David (super admin) or a customer user
        if (user.email === 'david@entwinedsignage.com.au' || user.email === 'david.joss@entwined.com.au') {
          // David gets full super admin permissions
          setUserRole('super_admin');
          setUserOUs(['*']);
          setManageableQueues(['*']); // Phase 3: Super admin can manage all queues
          const superAdminPermissions = getUserPermissions('super_admin');
          setUserPermissions(superAdminPermissions);
          console.log('🔑 David granted super admin permissions:', superAdminPermissions);
        } else {
          // Customer user - determine their role and OU scope from assignments
          const primaryRole = userRoles[0];
          const roleType = mapGoogleRoleToAppRole(primaryRole.roleId);
          setUserRole(roleType);
          
          // Extract accessible OUs from role assignments
          const accessibleOUs = userRoles.map(role => {
            if (role.scopeType === 'CUSTOMER') {
              return '*'; // Full customer access (rare)
            } else if (role.orgUnitId) {
              return role.orgUnitId;
            }
            return null;
          }).filter(ou => ou);
          
          setUserOUs(accessibleOUs.length > 0 ? accessibleOUs : ['/']); // Default to root if no specific OUs
          const customerPermissions = getUserPermissions(roleType);
          setUserPermissions(customerPermissions);
          console.log(`🔑 Customer user ${user.email} permissions:`, { role: roleType, ous: accessibleOUs, permissions: customerPermissions });
          
          // Phase 3: Load manageable queues for customer user (will be loaded separately)
          // Don't set here, let loadManageableQueues handle it
        }
      } else {
        console.log('❌ Role response not successful, using fallback');
        // Fallback based on user
        if (user.email === 'david@entwinedsignage.com.au' || user.email === 'david.joss@entwined.com.au') {
          setUserRole('super_admin');
          setUserOUs(['*']);
          setManageableQueues(['*']); // Phase 3: Super admin can manage all queues
          const superAdminPermissions = getUserPermissions('super_admin');
          setUserPermissions(superAdminPermissions);
          console.log('🔑 David fallback super admin permissions:', superAdminPermissions);
        } else {
          setUserRole('viewer');
          setUserOUs(['/']);
          setManageableQueues([]); // Phase 3: Viewer has no manageable queues
          const viewerPermissions = getUserPermissions('viewer');
          setUserPermissions(viewerPermissions);
          console.log(`🔑 Customer user ${user.email} fallback viewer permissions:`, viewerPermissions);
        }
      }
    } catch (error) {
      console.error('💥 Error loading user permissions:', error);
      // Error fallback based on user
      if (user.email === 'david@entwinedsignage.com.au' || user.email === 'david.joss@entwined.com.au') {
        setUserRole('super_admin');
        setUserOUs(['*']);
        setManageableQueues(['*']); // Phase 3: Super admin can manage all queues
        const superAdminPermissions = getUserPermissions('super_admin');
        setUserPermissions(superAdminPermissions);
        console.log('🔑 David error fallback super admin permissions:', superAdminPermissions);
      } else {
        setUserRole('viewer');
        setUserOUs(['/']);
        setManageableQueues([]); // Phase 3: Viewer has no manageable queues
        const viewerPermissions = getUserPermissions('viewer');
        setUserPermissions(viewerPermissions);
        console.log(`🔑 Customer user ${user.email} error fallback viewer permissions:`, viewerPermissions);
      }
    }
  };

  // Phase 3: Load manageable queues for the user
  const loadManageableQueues = async (user) => {
    console.log('🔄 Loading manageable queues for:', user.email);
    try {
      const response = await getManageableQueues();
      console.log('📋 Manageable queues response:', response);
      
      if (response.data?.success) {
        const queues = response.data.data || [];
        setManageableQueues(queues);
        console.log(`✅ User ${user.email} can manage ${queues.length} queues:`, queues);
      } else {
        console.log('❌ Manageable queues response not successful, using fallback');
        // Fallback: if super admin, all queues; otherwise none
        if (user.email === 'david@entwinedsignage.com.au' || user.email === 'david.joss@entwined.com.au') {
          setManageableQueues(['*']);
        } else {
          setManageableQueues([]);
        }
      }
    } catch (error) {
      console.error('💥 Error loading manageable queues:', error);
      // Error fallback
      if (user.email === 'david@entwinedsignage.com.au' || user.email === 'david.joss@entwined.com.au') {
        setManageableQueues(['*']);
      } else {
        setManageableQueues([]);
      }
    }
  };

  const hasPermission = (permission) => {
    console.log('🔍 hasPermission check:', { permission, userPermissions, result: userPermissions.includes(permission) || userPermissions.includes('*') });
    return userPermissions.includes(permission) || userPermissions.includes('*');
  };

  const canAccess = (targetOU) => {
    return canAccessOU(userOUs, targetOU);
  };

  const isSuperAdmin = () => {
    return userRole === 'super_admin' || userOUs.includes('*');
  };

  const isAuthorized = () => {
    return domainAccess.allowed;
  };

  const value = {
    user,
    userPermissions,
    userOUs,
    userRole,
    manageableQueues, // Phase 3: Queues user can manage
    loading,
    domainAccess,
    hasPermission,
    canAccess,
    isSuperAdmin,
    isAuthorized,
    reloadPermissions: () => {
      loadUserPermissions(user);
      loadManageableQueues(user);
    }
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}; 